import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import axiosInstance from '../../api/axiosInstance';
import { useProject } from '../../context/ProjectContext';
import useWorkspaceRouting from '../../hooks/useWorkspaceRouting';
import { buildWorkspaceDashboardPath } from '../../utils/workspaceRouteUtils';
import ViewTestPlanModal from '../test-plans/ViewTestPlanModal';
import RunTestPlanModal from '../test-plans/RunTestPlanModal';
import LinkTestSuitesModal from '../test-plans/LinkTestSuitesModal';
import {
  Breadcrumbs,
  ConfirmDialog,
  DataTable,
  EmptyState,
  ErrorState,
  LoadingState,
  MetricCard,
  PageScaffold,
  PriorityBadge,
  StatusBadge,
} from '../ui';
import '../../styles/rt2-plan-detail.css';
import { formatDate as formatUserDate } from '../../utils/dateTimeFormat';

const formatDate = (dateString) => formatUserDate(dateString, '—');

/**
 * BUILD · Test Plan detail — native RT2 surface (no legacy white embed).
 */
const PlanDetailPage = () => {
  const { planId } = useParams();
  const navigate = useNavigate();
  const { selectedProject } = useProject();
  const { getWorkspaceIdFromURL, getProjectIdFromURL } = useWorkspaceRouting();
  const projectId = getProjectIdFromURL() || selectedProject?.id;
  const projectName = selectedProject?.name || 'Project';

  const [plan, setPlan] = useState(null);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [runOpen, setRunOpen] = useState(false);
  const [linkOpen, setLinkOpen] = useState(false);
  const [unlinkOpen, setUnlinkOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const goBack = useCallback(() => {
    const workspaceId = getWorkspaceIdFromURL();
    if (workspaceId) {
      navigate(buildWorkspaceDashboardPath(workspaceId, projectId, 'test-plans'));
    } else {
      navigate('/dashboard/test-plans');
    }
  }, [navigate, getWorkspaceIdFromURL, projectId]);

  const openSuite = useCallback(
    (suiteId) => {
      if (!suiteId) return;
      const workspaceId = getWorkspaceIdFromURL();
      const path = `test-suites/${suiteId}`;
      if (workspaceId) {
        navigate(buildWorkspaceDashboardPath(workspaceId, projectId, path));
      } else {
        navigate(`/dashboard/${path}`);
      }
    },
    [navigate, getWorkspaceIdFromURL, projectId]
  );

  const load = useCallback(async () => {
    if (!projectId || !planId) {
      setError('Missing plan or project context.');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const [plansRes, memRes] = await Promise.all([
        axiosInstance.get(`/test_plan/test-plans/all_type?project_id=${projectId}`),
        axiosInstance.get(`/projects/list_project_members?project_id=${projectId}`).catch(() => null),
      ]);
      const raw = plansRes?.test_plans || [];
      const found = (Array.isArray(raw) ? raw : []).find(
        (p) => String(p.test_plan_id || p.id) === String(planId)
      );
      if (!found) {
        setError('Test plan not found.');
        setPlan(null);
      } else {
        let suites = [];
        try {
          const linkRes = await axiosInstance.get(
            `/test_plan/link_suites/${planId}?page=1&page_size=50`
          );
          suites = linkRes?.linked_suites || linkRes?.data || linkRes?.suites || [];
          if (!Array.isArray(suites)) suites = [];
        } catch {
          suites = found.linked_suites || [];
        }

        const memberList = memRes?.data || memRes?.members || memRes || [];
        const membersArr = Array.isArray(memberList) ? memberList : [];
        const assigneeId = found.assignee_id || found.assignee;
        let assigneeName = found.assignee || 'Unassigned';
        if (assigneeId && membersArr.length) {
          const m = membersArr.find((x) => String(x.user_id) === String(assigneeId));
          if (m?.name) assigneeName = m.name;
        }

        setPlan({
          test_plan_id: found.test_plan_id,
          id: found.test_plan_id,
          name: found.name,
          description: found.description,
          status: found.status || 'draft',
          priority: found.priority || 'medium',
          assignee: assigneeName,
          assignee_id: found.assignee_id || null,
          created_at: found.created_at,
          updated_at: found.updated_at,
          testCases: found?.test_case_count || 0,
          testSuites: suites.map((suite) =>
            typeof suite === 'object'
              ? {
                  suite_id: suite.suite_id || suite.id,
                  suite_name: suite.suite_name || suite.name || String(suite.suite_id),
                }
              : { suite_id: suite, suite_name: String(suite) }
          ),
          projectId: found.project_id,
          environment_setting_id: found.environment_setting_id ?? null,
        });
        setMembers(membersArr);
      }
    } catch (err) {
      console.error(err);
      setError(err?.message || 'Failed to load plan');
    } finally {
      setLoading(false);
    }
  }, [projectId, planId]);

  useEffect(() => {
    load();
  }, [load]);

  const suiteColumns = useMemo(
    () => [
      {
        key: 'suite_name',
        header: 'Suite',
        render: (row) => (
          <button type="button" className="rt2-link" onClick={() => openSuite(row.suite_id)}>
            {row.suite_name}
          </button>
        ),
      },
      {
        key: 'suite_id',
        header: 'ID',
        width: 100,
        render: (row) => <span className="rt2-muted">{row.suite_id}</span>,
      },
    ],
    [openSuite]
  );

  const confirmDelete = async () => {
    if (!plan?.test_plan_id) return;
    setDeleting(true);
    try {
      await axiosInstance.delete(`/test_plan/delete/${plan.test_plan_id}`);
      setDeleteOpen(false);
      goBack();
    } catch (err) {
      console.error(err);
      setError(err?.message || 'Failed to delete plan');
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <PageScaffold title="Test Plan" compact>
        <LoadingState />
      </PageScaffold>
    );
  }

  if (error || !plan) {
    return (
      <PageScaffold title="Test Plan" compact>
        <ErrorState description={error || 'Plan not found'} onRetry={load} />
        <button type="button" className="rt-btn rt-btn--secondary" onClick={goBack} style={{ marginTop: 12 }}>
          ← Plans
        </button>
      </PageScaffold>
    );
  }

  return (
    <PageScaffold
      title={plan.name || `Plan ${planId}`}
      description="Review linked suites, status and run this plan."
      breadcrumbs={
        <Breadcrumbs
          items={[
            { label: 'Build' },
            { label: 'Test Plans', path: 'test-plans' },
            { label: plan.name || `#${planId}` },
          ]}
          onNavigate={(path) => {
            if (path === 'test-plans') goBack();
          }}
        />
      }
      actions={
        <>
          <button type="button" className="rt-btn rt-btn--secondary" onClick={goBack}>
            ← Plans
          </button>
          <button type="button" className="rt-btn rt-btn--secondary" onClick={() => setEditOpen(true)}>
            Edit
          </button>
          <button type="button" className="rt-btn rt-btn--secondary" onClick={() => setLinkOpen(true)}>
            Link Suites
          </button>
          {plan.testSuites?.length > 0 && (
            <button type="button" className="rt-btn rt-btn--secondary" onClick={() => setUnlinkOpen(true)}>
              Unlink
            </button>
          )}
          <button type="button" className="rt-btn rt-btn--primary" onClick={() => setRunOpen(true)}>
            Run Plan
          </button>
        </>
      }
    >
      <div className="rt-plan-detail">
        <div className="rt-metric-grid">
          <MetricCard label="Linked suites" tone="accent" value={plan.testSuites?.length || 0} />
          <MetricCard label="Test cases" tone="violet" value={plan.testCases || 0} />
          <MetricCard
            label="Status"
            tone="success"
            value={<StatusBadge status={plan.status} />}
          />
          <MetricCard
            label="Priority"
            tone="warning"
            value={<PriorityBadge priority={plan.priority} />}
          />
        </div>

        <section className="rt-plan-detail__panel">
          <h3 className="rt-plan-detail__heading">Plan details</h3>
          <dl className="rt-plan-detail__grid">
            <div>
              <dt>Status</dt>
              <dd>
                <StatusBadge status={plan.status} />
              </dd>
            </div>
            <div>
              <dt>Priority</dt>
              <dd>
                <PriorityBadge priority={plan.priority} />
              </dd>
            </div>
            <div>
              <dt>Assignee</dt>
              <dd>{plan.assignee || 'Unassigned'}</dd>
            </div>
            <div>
              <dt>Created</dt>
              <dd>{formatDate(plan.created_at)}</dd>
            </div>
            <div>
              <dt>Updated</dt>
              <dd>{formatDate(plan.updated_at)}</dd>
            </div>
            <div className="rt-plan-detail__span">
              <dt>Description</dt>
              <dd>{plan.description?.trim() || '—'}</dd>
            </div>
          </dl>
        </section>

        <section className="rt-plan-detail__panel">
          <div className="rt-plan-detail__section-head">
            <h3 className="rt-plan-detail__heading">Linked test suites</h3>
            <button type="button" className="rt-btn rt-btn--secondary rt-btn--sm" onClick={() => setLinkOpen(true)}>
              + Link suites
            </button>
          </div>
          {plan.testSuites?.length ? (
            <DataTable
              columns={suiteColumns}
              rows={plan.testSuites}
              rowKey="suite_id"
              onRowClick={(row) => openSuite(row.suite_id)}
              emptyTitle="No suites linked"
            />
          ) : (
            <EmptyState
              title="No test suites linked"
              description="Link suites to this plan, then run from here or Executions."
              action={
                <button type="button" className="rt-btn rt-btn--primary" onClick={() => setLinkOpen(true)}>
                  Link Suites
                </button>
              }
            />
          )}
        </section>

        <div className="rt-plan-detail__danger">
          <button type="button" className="rt-btn rt-btn--danger rt-btn--sm" onClick={() => setDeleteOpen(true)}>
            Delete plan
          </button>
        </div>
      </div>

      <RunTestPlanModal
        isOpen={runOpen}
        onClose={() => setRunOpen(false)}
        testPlanIds={[plan.test_plan_id]}
        testPlanNames={[plan.name]}
        projectId={projectId}
        projectName={projectName}
        environmentSettingId={plan.environment_setting_id}
      />

      <LinkTestSuitesModal
        isOpen={linkOpen}
        onClose={() => setLinkOpen(false)}
        testPlan={plan}
        projectId={projectId}
        mode="link"
        onSuccess={() => {
          setLinkOpen(false);
          load();
        }}
      />

      <LinkTestSuitesModal
        isOpen={unlinkOpen}
        onClose={() => setUnlinkOpen(false)}
        testPlan={plan}
        projectId={projectId}
        mode="unlink"
        onSuccess={() => {
          setUnlinkOpen(false);
          load();
        }}
      />

      {editOpen && (
        <ViewTestPlanModal
          testPlan={plan}
          projectId={projectId}
          initialMembers={members}
          onClose={() => setEditOpen(false)}
          onEdit={async (updated) => {
            await axiosInstance.put(`/test_plan/update/${plan.test_plan_id}`, updated);
            setEditOpen(false);
            await load();
          }}
          onDelete={() => {
            setEditOpen(false);
            setDeleteOpen(true);
          }}
          mode="edit"
          variant="modal"
        />
      )}

      <ConfirmDialog
        open={deleteOpen}
        title="Delete plan?"
        body={`Delete "${plan.name}"? This cannot be undone.`}
        confirmLabel={deleting ? 'Deleting…' : 'Delete'}
        danger
        onCancel={() => setDeleteOpen(false)}
        onConfirm={confirmDelete}
      />
    </PageScaffold>
  );
};

export default PlanDetailPage;
